import { z } from "zod";
import { cognitoUuid } from "./validation-common";

// ─── Enums ───────────────────────────────────────────────────────────────────

export const ApplicationStatusSchema = z.enum([
  "draft",
  "submitted",
  "reviewing",
  "interview",
  "offered",
  "hired",
  "benched",
  "rejected",
]);
export type ApplicationStatus = z.infer<typeof ApplicationStatusSchema>;

export const ApplicationAnswerTypeSchema = z.enum([
  "text",
  "multiple_choice",
  "video",
]);
export type ApplicationAnswerType = z.infer<typeof ApplicationAnswerTypeSchema>;

// ─── Sub-schemas ─────────────────────────────────────────────────────────────

export const ApplicationAnswerSchema = z.object({
  applicationId: z.string(),
  questionId: z.string(),
  type: ApplicationAnswerTypeSchema,
  textAnswer: z.string().nullable(),
  mediaKey: z.string().nullable(),
});
export type ApplicationAnswer = z.infer<typeof ApplicationAnswerSchema>;

export const ApplicationContactInfoSchema = z
  .object({
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    photoMediaId: z.string().nullable(),
  })
  .nullable();
export type ApplicationContactInfo = z.infer<
  typeof ApplicationContactInfoSchema
>;

export const ApplicationPreferencesSchema = z.object({
  roles: z.array(z.string()).nullable(),
  availability: z.array(z.string()).nullable(),
  locations: z.array(z.string()).nullable(),
});
export type ApplicationPreferences = z.infer<
  typeof ApplicationPreferencesSchema
>;

// ─── Core Entity ─────────────────────────────────────────────────────────────

export const ApplicationSchema = z.object({
  applicationId: z.uuid(),
  candidateId: cognitoUuid,
  jobPostingId: z.uuid(),
  companyId: z.uuid(),
  status: ApplicationStatusSchema,
  contactInfo: ApplicationContactInfoSchema.optional(),
  identityInfo: z.record(z.string(), z.unknown()).nullable().optional(),
  matchScore: z.number().nullable().optional(),
  scheduleFit: z.number().nullable().optional(),
  aiScore: z.number().nullable().optional(),
  submittedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  answers: z.array(ApplicationAnswerSchema).optional(),
  preferences: ApplicationPreferencesSchema.optional(),
  statusChangedBy: z.string().optional(),
  statusChangeReason: z.string().nullable().optional(),
  offerDetails: z.unknown().nullable().optional(),
  offerSentAt: z.string().optional(),
  hiredAt: z.string().optional(),
  campaignId: z.string().optional(),
});
export type Application = z.infer<typeof ApplicationSchema>;

// ─── Response Schemas ─────────────────────────────────────────────────────────

export const ApplicationListResponseSchema = z.object({
  applications: z.array(ApplicationSchema),
});
export type ApplicationListResponse = z.infer<
  typeof ApplicationListResponseSchema
>;

export const CreateApplicationResponseSchema = ApplicationSchema;
export type CreateApplicationResponse = z.infer<
  typeof CreateApplicationResponseSchema
>;

export const ApplicationAckSchema = z.object({
  message: z.string(),
  applicationId: z.uuid(),
});
export type ApplicationAck = z.infer<typeof ApplicationAckSchema>;

export const BenchAckSchema = z.object({
  message: z.string(),
  applicationId: z.uuid(),
  status: z.literal("benched"),
});
export type BenchAck = z.infer<typeof BenchAckSchema>;

// ─── Request Schemas ──────────────────────────────────────────────────────────

export const CreateApplicationRequestSchema = z.object({
  jobPostingId: z.uuid(),
  campaignId: z.uuid().optional(),
});
export type CreateApplicationRequest = z.infer<
  typeof CreateApplicationRequestSchema
>;

export const UpdateApplicationStatusRequestSchema = z.object({
  status: ApplicationStatusSchema,
  reason: z.string().optional(),
});
export type UpdateApplicationStatusRequest = z.infer<
  typeof UpdateApplicationStatusRequestSchema
>;

export const ApplicationAnswerPayloadSchema = z.object({
  type: ApplicationAnswerTypeSchema,
  textAnswer: z.string().optional(),
  mediaKey: z.string().optional(),
});
export type ApplicationAnswerPayload = z.infer<
  typeof ApplicationAnswerPayloadSchema
>;
